import React, { memo, useEffect, useState } from "react";
import * as d3 from "d3";
import { useRef } from "react";

interface TreeNode {
    name: string;
    children?: TreeNode[];
    _children?: TreeNode[];
    x?: number;
    y?: number;
    x0?: number;
    y0?: number;
    id?: number;
    depth?: number;
    parent?: TreeNode;
    selected?: boolean;
  }
  
  interface ExtendedHierarchyPointNode<T> extends d3.HierarchyPointNode<T> {
    x0?: number;
    y0?: number;
    selected?: boolean;
  }

const useRadialTreeInteractor = ({treeData}:any) => {

    const containerRef = useRef(null)
    const [svgGroup,setSvgGroup] = useState(null)
    const [svgBase,setSvgBase] = useState(null)
    const [root,setRoot] = useState(null)
    let width = window.innerWidth - 20;
  let height = window.innerHeight - 20;
    // current pan, zoom, and rotation
  let curX = width / 2;
  let curY = height / 2;
  let curZ = 1.0; // current zoom
  let curR = 270; 

  function calculateMaxTextLengthByDepth(root:any[]) {
    const maxLengthByDepth = {} as Record<number, number>;
    
    function traverse(node:any, depth:number) {
      if (!maxLengthByDepth[depth] || node.data.name.length > maxLengthByDepth[depth]) {
        maxLengthByDepth[depth] = node.data.name.length;
      }
      if (node.children) {
        node.children.forEach((child:any) => traverse(child, depth + 1));
      }
    }
    
    traverse(root, 0);
    return maxLengthByDepth;
  }

  function adjustNodePositions(root: any) {
    const NODE_DIAMETER = 5;
    const TEXT_PADDING = 50;
    const DEPTH_PADDING = 20;  // Increase this for more space between depth levels
    const SIBLING_ANGLE_PADDING = 5;  // Angle padding between siblings (in radians)
  
    function getMaxTextLength(nodes: any) {
      return Math.max(...nodes.map((n: any) => n.data.name.length));
    }
  
    const nodesByDepth = d3.group(root.descendants(), (d: any) => d.depth);
    let cumulative = 0;
  
    nodesByDepth.forEach((nodes, depth) => {
      const maxTextLength = getMaxTextLength(nodes);
      const levelWidth = NODE_DIAMETER+maxTextLength * 2 + TEXT_PADDING;
  
      cumulative += depth === 0 ? 0 : levelWidth + DEPTH_PADDING;
  
      // Sort nodes by their x position to maintain their order
      nodes.sort((a: any, b: any) => a.x - b.x);
  
      // Calculate total angle span for this depth
      const totalAngleSpan = (nodes[nodes.length - 1] as any).x - (nodes[0] as any).x;
      
      // Distribute nodes with angular padding
      nodes.forEach((node: any, i: number) => {
        if (i === 0) {
          node.x = node.x;  // Keep the first node at its original position
        } else {
          const prevNode = nodes[i - 1];
          const minAngle = (prevNode as any).x + SIBLING_ANGLE_PADDING * (180 / Math.PI);
          node.x = Math.max(node.x, minAngle);
        }
        node.y = cumulative;
        // if (node._children) {
        //   adjustCollapsedChildren(node, cumulative);
        // }
      });
  
      // Adjust the overall spread if needed
      const newSpan = (nodes[nodes.length - 1] as any).x - (nodes[0] as any).x;
      if (newSpan > totalAngleSpan) {
        const scale = totalAngleSpan / newSpan;
        nodes.forEach((node: any, i: number) => {
          if (i !== 0) {  // Keep the first node at its position
            node.x = (nodes[0] as any).x + (node.x - (nodes[0] as any).x) * scale;
          }
        });
      }

      // function adjustCollapsedChildren(node: any, parentY: number) {
      //   if (node._children) {
      //     node._children.forEach((child: any) => {
      //       child.y = parentY;
      //       adjustCollapsedChildren(child, parentY);
      //     });
      //   }
      // }
    });
  }

  // function adjustNodePositions(root:any) {
  //   const NODE_DIAMETER = 4;
  //   const TEXT_PADDING = 10;
  //   const DEPTH_PADDING = 0;  // Additional padding between depth levels
  
  //   function getMaxTextLength(nodes:any) {
  //     return Math.max(...nodes.map((n:any) => n.data.name.length));
  //   }
  
  //   const nodesByDepth = d3.group(root.descendants(), (d:any) => d.depth);
  //   let cumulative = 0;
  
  //   nodesByDepth.forEach((nodes, depth) => {
  //     const maxTextLength = getMaxTextLength(nodes);
  //     const levelWidth = NODE_DIAMETER + maxTextLength * 6 + TEXT_PADDING;
      
  //     cumulative += depth === 0 ? 0 : levelWidth + DEPTH_PADDING;
  
  //     nodes.forEach((node:any) => {
  //       node.y = cumulative;
  //     });
  //   });
  // }

    useEffect(() => {

      const DURATION = 700; // d3 animation duration
  const STAGGERN = 4; // delay for each node
  const STAGGERD = 200; // delay for each depth
  const NODE_DIAMETER = 4; // diameter of circular nodes
  const MIN_ZOOM = 0.5; // minimum zoom allowed
  const MAX_ZOOM = 10; // maximum zoom allowed
  const HAS_CHILDREN_COLOR = "lightsteelblue";
  const SELECTED_COLOR = "#a00"; // color of selected node
  const ZOOM_INC = 0.04; // zoom factor per animation frame
  const PAN_INC = 3; //  pan per animation frame
  const ROT_INC = 0.3; // rotation per animation frame

  var root1 = d3.tree<TreeNode>().size([360, Math.min(width, height) / 2 - 120])(
    d3.hierarchy(treeData)
  ) as ExtendedHierarchyPointNode<TreeNode>;
  const maxTextLengthByDepth = calculateMaxTextLengthByDepth(root1 as any);

  let counter = 0; // node ids
  let curNode: TreeNode | null = null; // currently selected node
  let curPath: TreeNode[] = []; // array of nodes in the path to the currently selected node

  // size of the diagram
  

  // current rotation

  // keyboard key codes
  const KEY_PLUS = 187; // + (zoom in)
  const KEY_MINUS = 189; // - (zoom out)
  const KEY_SLASH = 191; // / (slash)
  const KEY_PAGEUP = 33; // (rotate CCW)
  const KEY_PAGEDOWN = 34; // (rotate CW)
  const KEY_LEFT = 37; // left arrow
  const KEY_UP = 38; // up arrow
  const KEY_RIGHT = 39; // right arrow
  const KEY_DOWN = 40; // down arrow
  const KEY_SPACE = 32; // (expand node)
  const KEY_RETURN = 13; // (expand tree)
  const KEY_HOME = 36; // (center root)
  const KEY_END = 35; // (center selection)
  const KEY_TOGGLE_LEVEL = 25 // (toggle level)

  // d3 diagonal projection for use by the node paths

  
  const diagonal = d3
    .linkRadial<any, any>()
    .angle((d) => ((d as any).x / 180) * Math.PI) // Convert degrees to radians
    .radius((d) => (d as any).y);

    const radius = Math.min(width, height) / 2 - 200; // Adjust this as needed

  // d3 tree layout
  var tree = d3
    .tree()
    // .nodeSize([4.5, 120])
    .size([360, radius])
    .separation(function (a, b) {
      return a.depth === 0 ? 1 : (a.parent === b.parent ? 1 : 2) / a.depth;
    });

    var checksvgpresence = svgBase = d3
    .select(containerRef.current)
    .select("svg")


    if(!checksvgpresence.size()){
        // define the svgBase, attaching a class for styling and the zoomListener
  var svgBase = d3
  .select(containerRef.current)
  .append("svg")
  .attr("width", width)
  .attr("height", height)
  .on("mousedown", mousedown)
  .on("mouseout", (event,d) => resetOpacity())

// Group which holds all nodes and manages pan, zoom, rotate
var svgGroup = svgBase
  .append("g")
  .attr("transform", `translate(${curX}, ${curY})`);

d3.select(containerRef.current) // set up document events
  .on("wheel", wheel) // zoom, rotate
  .on("keydown", keydown)
  .on("keyup", keyup);
d3.select(window).on("resize", resize);
d3.selectAll(".button").on("mousedown", tooldown).on("mouseup", toolup);
d3.select("#selection").on("mousedown", switchroot);
d3.select("#contextmenu").on("mouseup", menuSelection);

// Define the data root
var root = d3.tree<TreeNode>().size([360, Math.min(width, height) / 2 - 120])(
  d3.hierarchy(treeData)
) as ExtendedHierarchyPointNode<TreeNode>;
var rootDup=root
root.x0 = curY;
root.y0 = 0;

setSvgGroup(svgGroup as any)
setSvgBase(svgBase as any)
setRoot(root as any)
 // current selected node
selectNode(root);
  update(root, true); // Layout the tree initially and center on the root node
    }

  

  // Collapse all children of root's children before rendering
  // if (root.children) {
  //   root.children.forEach(function(child) {
  //       collapseTree(child);
  //   });
  // }

  

  // update the tree
  // source - source node of the update
  // transition - whether to do a transition
  function update(source: any, transition: boolean=false) {
    const duration = transition ? DURATION : 0;
    // Compute the new tree layout.
    root = d3.tree<TreeNode>().size([360, Math.min(width, height) / 2 - 120])(
      root
    ) as ExtendedHierarchyPointNode<TreeNode>;
    
    adjustNodePositions(root as any)
    const nodes = root.descendants();
    const links = root.links();

    console.log("the nodes are ",nodes)
    // Update the view
    svgGroup
      .transition()
      .duration(duration)
      .attr(
        "transform",
        `rotate(${curR} ${curX} ${curY})translate(${curX} ${curY})scale(${curZ})`
      );

    // Update the nodes…
    const node = svgGroup
      .selectAll<SVGGElement, d3.HierarchyPointNode<TreeNode>>("g.node")
      .data(nodes, (d: any) => d.data.id || (d.data.id = ++counter));

    // Enter any new nodes at the parent's previous position
    const nodeEnter = node
      .enter()
      .append("g")
      .attr("class", "node")
      .attr(
        "transform",
        (d) => `rotate(${source.x0! - 90})translate(${source.y0!})`
      )
      .on("click", (event, d: any) => {
        click(event, d)
      })
      .on("mouseover", (event,d) => highlightNode(event,d) )
      .on("dblclick", (event, d) => dblclick(event, d))
      .on("contextmenu", (event, d) => showContextMenu(event, d));

    nodeEnter
      .append("circle")
      .attr("r", 1e-6)
      .style("fill", (d) => ((d as any)._children ? HAS_CHILDREN_COLOR : "white"))
      .attr("stroke-width", (d) => {
        return (d.selected ? 3 : 1.5)
      }).attr("stroke", (d) => (d.selected ? SELECTED_COLOR : "steelblue"))

    // nodeEnter
    // .append("text")
    // .text((d) => d.data.name)
    // .style("opacity", 0.9)
    // .style("fill-opacity", 0);
  
   // Adding text with transform to position before or after the circle
nodeEnter
.append("text")
.text((d) => d.data.name)
.style("opacity", 0.9)
.style("fill-opacity", 0)
.attr("transform", (d) => {
  // Adjust text position based on whether the node has children
  if (d.children) {
    return "translate(-10, 0)"; // Position text before the circle
  } else {
    return "translate(10, 0)"; // Position text after the circle
  }
})
.attr("text-anchor", (d:any) => (d.children || d._children ? "end" : "start")) // Align text
.style("font-size", "12px") // Customize as needed

// nodeEnter
//   .append("text")
//   .attr("dy", ".35em")
//   .attr("x", function(d) {
//     const padding = 10; // Base padding
//     const maxLength = maxTextLengthByDepth[d.depth] * 6; // Approximate width of text
//     return d.children || d.children ? 
//       -maxLength - padding - NODE_DIAMETER : // Before circle for parent nodes
//       NODE_DIAMETER + padding; // After circle for leaf nodes
//   })
//   .attr("text-anchor", function(d) {
//     return d.children || d.children ? "end" : "start";
//   })
//   .text(function(d) { return d.data.name; });

    // Update existing nodes
    node
      .select("circle")
      .attr("r", NODE_DIAMETER * reduceZ())
      .style("fill", (d:any) => (d?._children ? HAS_CHILDREN_COLOR : "white"))
      .attr("stroke", (d) => (d.selected ? SELECTED_COLOR : "steelblue"))
      .attr("stroke-width", (d) => {
        return (d.selected ? 3 : 1.5)
      });

    // node
    //   .select("text")
    //   .attr("text-anchor", (d) => ((d.x + curR) % 360 <= 180 ? "start" : "end"))
    //   .attr(
    //     "transform",
    //     (d) =>
    //       ((d.x + curR) % 360 <= 180
    //         ? "translate(8)scale("
    //         : "rotate(180)translate(-8)scale(") +
    //       reduceZ() +
    //       ")"
    //   )
    //   .attr("fill", (d) => (d.selected ? SELECTED_COLOR : "black"))
    //   .attr("dy", ".35em");
    console.log("The intial Loading dup root")
    nodeEnter
  .select("text")
  .attr("text-anchor", (d) => ((d.x + curR) % 360 <= 180 ? "start" : "end"))
  .attr(
    "transform",
    (d) => {
      const textLength = d.data.name.length * 6+10;
          const translateBeforeCircle = "translate(-8)";
          const translateAfterCircle = `translate(${textLength})`;
          const translateAfterCircleNeg = `translate(-${textLength})`;
          const rotate180 = "rotate(180)";
          const scale = `scale(${reduceZ()})`;          
          if (d.children) {
            return ((d.x + curR) % 360 <= 180
              ? `${translateAfterCircleNeg}${scale}`
              : `${rotate180}${translateAfterCircle}${scale}`);
          } else {
            return ((d.x + curR) % 360 <= 180
              ? `translate(8)${scale}`
              : `${rotate180}${translateBeforeCircle}${scale}`);
          }

    }
  )
  .attr("fill", (d) => (d.selected ? SELECTED_COLOR : "black"))
  .attr("dy", ".35em");

  

    // Transition nodes to their new position
    const nodeUpdate = node.merge(nodeEnter).transition().duration(duration);

    if (transition) {
      nodeUpdate.delay((d, i) => {
        const curNodeDepth =
          curNode && typeof curNode.depth === "number" ? curNode.depth : 0;
        return i * STAGGERN + Math.abs(d.depth - curNodeDepth) * STAGGERD;
      });
    } else {
      nodeUpdate.delay(0);
    }

    nodeUpdate.attr("transform", (d) => `rotate(${d.x - 90})translate(${d.y})`);

    nodeUpdate.select("circle").attr("r", NODE_DIAMETER * reduceZ());

    nodeUpdate.select("text").style("fill-opacity", 1);
      
    // Transition exiting nodes to the parent's new position and remove
    const nodeExit = node
      .exit<d3.HierarchyPointNode<TreeNode>>()
      .transition()
      .duration(duration);
    if (transition) {
      nodeExit.delay((d, i) => i * STAGGERN);
    } else {
      nodeExit.delay(0);
    }

    nodeExit
      .attr(
        "transform",
        (d) => `rotate(${source.x - 90})translate(${source.y})`
      )
      .remove();

    nodeExit.select("circle").attr("r", 0);
    nodeExit.select("text").style("fill-opacity", 0);

    const link = svgGroup
    .selectAll<SVGPathElement, d3.HierarchyLink<TreeNode>>('path.link')
    .data(links, (d: any) => d.target.data.id);

  // Enter any new links
  const linkEnter = link.enter().insert('path', 'g').attr('class', 'link');

  // Update existing links
  const linkUpdate = linkEnter.merge(link);

  // Transition links to their new position
  linkUpdate
    .transition()
    .duration(transition ? DURATION : 0)
    .attr('d', (d) => diagonal(d));

  // Transition exiting links to the parent's new position
  link.exit().transition().duration(transition ? DURATION : 0).remove();

    
    // Update the links…
  //   const link = svgGroup
  //     .selectAll<SVGPathElement, d3.HierarchyLink<TreeNode>>("path.link")
  //     .data(links, (d: any) => {
  //       return d.target.data.id
  //     });

  //   // // Enter any new links at the parent's previous position
  //   link
  //     .enter()
  //     .insert("path", "g")
  //     .attr("class", "link")
  //     .attr("d", (d) => {
  //       var o = {
  //         x: source.x0,
  //         y: source.y0
  //     };
  //       return  diagonal(d);
  //     })
  //     .transition()
  //     .duration(duration)

  //     // if(transition)
  //     // {
  //     //   link.transition().duration(duration)
  //     // .delay(function(d, i) {
  //     //   let currentNodeDepth = curNode && curNode?.depth && curNode.depth!=undefined ? curNode.depth : 0
  //     //     return i * STAGGERN +
  //     //       Math.abs(d.source.depth - currentNodeDepth) * STAGGERD;
  //     //       // Math.max(0, d.source.depth - curNode.depth) * STAGGERD;
  //     //     })
  //     // .attr('d', diagonal);
  //     // }else{
  //     //   link.transition().duration(duration)
  //     //   .delay(0)
  //     //   .attr('d', diagonal);
  //     // }
  //      // Transition exiting nodes to the parent's new position

  //   link.exit().transition().duration(duration)
  //   .attr('d', function() {
  //     var o = {
  //       x: source.x0,
  //       y: source.y0
  //     };
  //     return diagonal({
  //       source: o,
  //       target: o
  //     });
  // }).remove();

    // Stash the old positions for transition
    nodes.forEach((d: any) => {
      d.x0 = d.x;
      d.y0 = d.y;
    });
  }

  function resetOpacity() {
    svgGroup
      .selectAll<SVGGElement, d3.HierarchyPointNode<TreeNode>>("g.node")
      .style("opacity", 1);
  
    svgGroup
      .selectAll<SVGPathElement, d3.HierarchyLink<TreeNode>>("path.link")
      .style("opacity", 1);
  }

  // Function to highlight a node and its subtree
  function highlightNode(event:any,selectedNode: any) {
    event.preventDefault();
    const descendants = selectedNode.descendants(); // Get all descendants of the selected node
    
    // Set low opacity for all nodes and links
    svgGroup.selectAll<SVGGElement, d3.HierarchyPointNode<TreeNode>>("g.node")
      .style("opacity", 0.2); // Low opacity for all nodes
    
    svgGroup.selectAll<SVGPathElement, d3.HierarchyLink<TreeNode>>("path.link")
      .style("opacity", 0.2); // Low opacity for all links
    
    // Highlight the selected node and its descendants
    svgGroup
      .selectAll<SVGGElement, d3.HierarchyPointNode<TreeNode>>("g.node")
      .filter((d) => descendants.includes(d))
      .style("opacity", 1); // High opacity for selected node and its descendants
  
    // Highlight the links connected to the selected node and its descendants
    svgGroup
      .selectAll<SVGPathElement, d3.HierarchyLink<TreeNode>>("path.link")
      .filter((d) => descendants.includes(d.source) || descendants.includes(d.target))
      .style("opacity", 1) // High opacity for the connecting links
      .style("stroke-width", "2px"); // Optional: increase stroke width
  }  
  

  // Helper functions for collapsing and expanding nodes

  // Toggle expand / collapse
  function toggle(d: any) {
    if (d.children) {
      d._children = d.children;
      d.children = null;
    } else if (d._children) {
      d.children = d._children;
      d._children = null;
    }
  }

  function toggleTree(d: any) {
    if (d.children) {
      collapseTree(d);
    } else {
      expandTree(d);
    }
    
  }

  function expand(d: any) {
    if (d._children) {
      d.children = d._children;
      d._children = null;
    }
    
  }

  // expand all children, whether expanded or collapsed
  function expandTree(d: any) {
    if (d._children) {
      d.children = d._children;
      d._children = null;
    }
    if (d.children) {
      d.children.forEach(expandTree);
    }
    
  }

  function collapse(d: any) {
    if (d.children) {
      d._children = d.children;
      d.children = null;
    }
    
  }

  // collapse all children
  function collapseTree(d: any) {
    if (d.children) {
      d._children = d.children;
      d.children = null;
    }
    if (d._children) {
      d._children.forEach(collapseTree);
    }
    
  }

  // expand one level of tree
  function expand1Level(d: any) {
    var q = [d]; // non-recursive
    var cn;
    var done = null;
    while (q.length > 0) {
      cn = q.shift();
      if (done !== null && done < cn.depth) {
        return;
      }
      if (cn._children) {
        done = cn.depth;
        cn.children = cn._children;
        cn._children = null;
        cn.children.forEach(collapse);
      }
      if (cn.children) {
        q = q.concat(cn.children);
      }
    }
    
    // no nodes to open
  }

  // highlight selected node
  function selectNode(node: any) {
    if (curNode) {
      delete curNode.selected;
    }
    curNode = node;
    if (curNode) curNode.selected = true;
    curPath = []; // filled in by fullpath
    d3.select("#selection").html(fullpath(curNode));
    
  }

  // for displaying full path of node in tree
  function fullpath(d: any, idx: any = 0): any {
    idx = idx || 0;
    curPath.push(d);
    return (
      (d.parent ? fullpath(d.parent, curPath.length) : "") +
      '/<span class="nodepath' +
      (d.data.name === root.data.name ? " highlight" : "") +
      '" data-sel="' +
      idx +
      '" title="Set Root to ' +
      d.data.name +
      '">' +
      d.data.name +
      "</span>"
    );
  }

  // d3 event handlers

  function switchroot(event: any) {
    event.preventDefault();
    var pathelms = document.querySelectorAll("#selection .nodepath");
    for (var i = 0; i < pathelms.length; i++) {
      pathelms[i].classList.remove("highlight");
    }
    var target = event.target;
    var node = curPath[+target.dataset.sel];
    if (event.shiftKey) {
      if (curNode !== node) {
        selectNode(node);
      }
    } else {
      root = node as ExtendedHierarchyPointNode<TreeNode>;
      target.classList.add("highlight");
    }
    update(root, true);
  }

  function resize() {
    // window resize
    var oldwidth = width;
    var oldheight = height;
    width = window.innerWidth - 20;
    height = window.innerHeight - 20;
    tree.size([360, Math.min(width, height) / 2 - 120]);
    svgBase.attr("width", width).attr("height", height);
    curX += (width - oldwidth) / 2;
    curY += (height - oldheight) / 2;
    svgGroup.attr(
      "transform",
      "rotate(" +
        curR +
        " " +
        curX +
        " " +
        curY +
        ")translate(" +
        curX +
        " " +
        curY +
        ")scale(" +
        curZ +
        ")"
    );
    update(root, true);
  }

  function click(event: any, d: any) {
    // select node
    if (event.defaultPrevented) {
      return;
    } // suppressed
    event.preventDefault();
    if (d.children) {
      d._children = d.children;
      d.children = null;
    } else if (d._children) {
      d.children = d._children;
      d._children = null;
    }
    selectNode(d);
    console.log("The selected node is ", d)
    update(root, true);
  }

  function dblclick(event: any, d: any) {
    // Toggle children of node
    if (event.defaultPrevented) {
      return;
    } // click suppressed
    event.preventDefault();
    if (event.shiftKey) {
      expand1Level(d); // expand node by one level
    } else {
      toggle(d);
    }
    update(d, true);
  }

  function tooldown(event: any, d: any) {
    // tool button pressed
    event.preventDefault();
    d3.select(event.target).on("mouseout", toolup);
    var key = +event.target.dataset.key;
    keydown(event);
  }

  function toolup(event: any) {
    // tool button released
    event.preventDefault();
    d3.select(event.target).on("mouseout", null);
    const key = Math.abs(+event.target.dataset.key);
    keyup(event, key);
  }

  // right click, show context menu and select this node
  function showContextMenu(event: any, d: any) {
    event.preventDefault();
    d3.selectAll(".expcol").text(d.children ? "Collapse" : "Expand");
    d3.select("#contextmenu")
      .style("left", event.pageX + 3 + "px")
      .style("top", event.pageY + 8 + "px")
      .style("display", "block");
    d3.select(document).on("mouseup", hideContextMenu);
    selectNode(d);
    update(d, true);
  }

  function hideContextMenu() {
    d3.select("#contextmenu").style("display", "none");
    d3.select(document).on("mouseup", null);
  }

  function menuSelection(event: any) {
    event.preventDefault();
      // const key = +event.target.dataset.key; // Extract the key from the dataset
      // const shift = event.shiftKey; // Get the shift state from the event
    keydown(event); // Pass event, key, and shift
  }

  var startposX: any, startposY: any; // initial position on mouse button down for pan

  function mousedown(event: any) {
    // pan
    event.preventDefault();
    if (event.which !== 1 || event.ctrlKey) {
      return;
    } // ingore other mouse buttons
    startposX = curX - event.clientX;
    startposY = curY - event.clientY;
    d3.select(document).on("mousemove", mousemove, true);
    d3.select(document).on("mouseup", mouseup, true);
  }

  function mousemove(event: any) {
    event.preventDefault();
    curX = startposX + event.clientX;
    curY = startposY + event.clientY;
    setview();
  }

  function mouseup() {
    d3.select(document).on("mousemove", null);
    d3.select(document).on("mouseup", null);
  }

  var keysdown: any = []; // which keys are currently down
  var moveX = 0,
    moveY = 0,
    moveZ = 0,
    moveR = 0; // animations
  var aniRequest: any = null;

  function wheel(event: any) {
    // mousewheel
    var dz, newZ;
    var slow = event.altKey ? 0.25 : 1;
    if (event.wheelDeltaY !== 0) {
      // up-down
      dz = Math.pow(1.2, event.wheelDeltaY * 0.001 * slow);
      newZ = limitZ(curZ * dz);
      dz = newZ / curZ;
      curZ = newZ;

      curX -= (event.clientX - curX) * (dz - 1);
      curY -= (event.clientY - curY) * (dz - 1);
      setview();
    }
    if (event.wheelDeltaX !== 0) {
      // left-right
      curR = limitR(curR + event.wheelDeltaX * 0.01 * slow);
      update(root, true);
    }
  }

  // keyboard shortcuts
  function keydown(event: any) {
    const key = Math.abs(parseInt(event.target.dataset.key)) || event.which; // Use the event parameter directly
    const shift = event.shiftKey;
    let parch; // parent's children
    const slow = event.altKey ? 0.25 : 1; // Use event directly to check altKey

    if (keysdown.indexOf(key) >= 0) {
      return;
    } // defeat auto repeat
    switch (key) {
      case KEY_PLUS: // zoom in
        moveZ = ZOOM_INC * slow;
        break;
      case KEY_MINUS: // zoom out
        moveZ = -ZOOM_INC * slow;
        break;
      case KEY_SLASH: // toggle root to selection
        root =
          root === curNode
            ? (rootDup as ExtendedHierarchyPointNode<TreeNode>)
            : (curNode as ExtendedHierarchyPointNode<TreeNode>);
            
        update(root, true);
        curPath = []; // filled in by fullpath
        d3.select("#selection").html(fullpath(curNode));
        return;
      case KEY_PAGEUP: // rotate counterclockwise
        moveR = -ROT_INC * slow;
        break;
      case KEY_PAGEDOWN: // rotate clockwise
        moveR = ROT_INC * slow;
        break;
      case KEY_LEFT: // left arrow
        if (shift) {
          // move selection to parent
          if (!curNode) {
            selectNode(root);
          } else if (curNode.parent) {
            selectNode(curNode.parent);
          }
          update(curNode, true);
          return;
        }
        moveX = -PAN_INC * slow;
        break;
      case KEY_UP: // up arrow
        if (shift) {
          // move selection to previous child
          if (!curNode) {
            selectNode(root);
          } else if (curNode.parent) {
            parch = curNode.parent.children;
            if(parch!=undefined){
              highlightNode(event,parch[(parch.indexOf(curNode) +
                parch.length - 1) % parch.length])
                selectNode(parch[(parch.indexOf(curNode) +
                  parch.length - 1) % parch.length]);
            }
            
          }
          console.log("going to update the curNode")
          update(curNode);
          return;
        }
        moveY = -PAN_INC * slow;
        break;
      case KEY_RIGHT: // right arrow
        if (shift) {
          // move selection to first/last child
          if (!curNode) {
            selectNode(root);
          } else {
            if (curNode.children) {
              selectNode(
                curNode.children[event.altKey ? curNode.children.length - 1 : 0]
              );
            }
          }
          update(curNode);
          return;
        }
        moveX = PAN_INC * slow;
        break;
      case KEY_DOWN: // down arrow
        if (shift) {
          // move selection to next child
          if (!curNode) {
            selectNode(root);
          } else if (curNode.parent) {
            parch = curNode.parent.children;
            if (parch)
            {
              highlightNode(event,parch[(parch.indexOf(curNode) + 1) % parch.length])
              selectNode(parch[(parch.indexOf(curNode) + 1) % parch.length]);
            }
          }
          update(curNode);
          return;
        }
        moveY = PAN_INC * slow;
        break;
      case KEY_SPACE: // expand/collapse node
        if (!curNode) {
          selectNode(root);
        }
        toggle(curNode);
        update(curNode);
        return;
      case KEY_RETURN: // expand/collapse tree
        if (!curNode) {
          selectNode(root);
        }
        if (shift) {
          expandTree(curNode);
        } else {
          expand1Level(curNode);
        }
        update(curNode, true);
        return;
      case KEY_HOME: // reset transform
        if (shift) {
          root = treeData as ExtendedHierarchyPointNode<TreeNode>;
        }
        curX = width / 2;
        curY = height / 2;
        curR = limitR(90 - root.x);
        // curZ = 1;
        // curZ = Math.min(width, height) / (root.y * 2 + 200); // Adjust zoom to fit
        // update(root, true);
        setview()
        return;
      case KEY_END: // zoom to selection
        if (!curNode) {
          return;
        }
        curX = width / 2 - (curNode.y ? curNode.y * curZ : 0);
        curY = height / 2;
        curR = limitR(90 - (curNode.x ?? 0));
        // update(curNode, true);
        setview()
        return;
      case KEY_TOGGLE_LEVEL: // toggle depth
        if (!curNode) {
          return;
        }
        toggleDepth(curNode as any);
        update(curNode, true);
        return;
      default:
        return; // ignore other keys
    } // break jumps to here

    keysdown.push(key);
    // start animation if anything happening
    if (keysdown.length > 0 && aniRequest === null) {
      
      aniRequest = requestAnimationFrame(frame);
    }
  }

  function keyup(event: any, key: any) {
    
    key = key || Math.abs(parseInt(event.target.dataset.key)) || event.which; // Use the event parameter directly
    const pos = keysdown.indexOf(key);
    if (pos < 0) {
      return;
    }

    switch (key) {
      case KEY_PLUS: // zoom in
      case KEY_MINUS: // zoom out
        moveZ = 0;
        break;
      case KEY_PAGEUP: // rotate counterclockwise
      case KEY_PAGEDOWN: // rotate clockwise
        moveR = 0;
        break;
      case KEY_LEFT: // left arrow
      case KEY_RIGHT: // right arrow
        moveX = 0;
        break;
      case KEY_UP: // up arrow
      case KEY_DOWN: // down arrow
        moveY = 0;
        break;
    }

    keysdown.splice(pos, 1); // remove key
    if (keysdown.length > 0 || aniRequest === null) {
      return;
    }

    cancelAnimationFrame(aniRequest);
    aniRequest = aniTime = null;
  }

  var aniTime: any = null;

  // update animation frame
  function frame(frametime: any) {
    var diff = aniTime ? (frametime - aniTime) / 16 : 0;
    aniTime = frametime;
    
    var dz = Math.pow(1.2, diff * moveZ);
    var newZ = limitZ(curZ * dz);
    dz = newZ / curZ;
    curZ = newZ;
    curX += diff * moveX - (width / 2 - curX) * (dz - 1);
    curY += diff * moveY - (height / 2 - curY) * (dz - 1);
    curR = limitR(curR + diff * moveR);
    setview();
    aniRequest = requestAnimationFrame(frame);
  }

  // enforce zoom extent
  function limitZ(z: any) {
    return Math.max(Math.min(z, MAX_ZOOM), MIN_ZOOM);
  }

  function toggleDepth(node: ExtendedHierarchyPointNode<TreeNode>) {
    const depthToToggle = node.depth;
    const root = node.ancestors().pop() || node; // Get the root node
  
    function toggle(d: any) {
      if (d.depth === depthToToggle) {
        if (d.children) {
          d._children = d.children;
          d.children = null;
        } else if (d._children) {
          d.children = d._children;
          d._children = null;
        }
      }
      if (d.children) {
        d.children.forEach(toggle);
      }
    }
  
    toggle(root);
    update(root, true);
  }

  // keep rotation between 0 and 360
  function limitR(r: any) {
    return (r + 360) % 360;
  }

  // limit size of text and nodes as scale increases
  function reduceZ() {
    return Math.pow(1.1, -curZ);
  }

  // set view with no animation
  function setview() {
    
    svgGroup.attr(
      "transform",
    `rotate(${curR} ${curX} ${curY}) translate(${curX}, ${curY}) scale(${curZ})`
    );
    svgGroup
      .selectAll("text")
      .attr("text-anchor", function (d: any) {
        return (d.x + curR) % 360 <= 180 ? "start" : "end";
      })
      .attr(
        "transform",
        (d:any) => {
          const textLength = d.data.name.length * 6+10;
          const translateBeforeCircle = "translate(-8)";
          const translateAfterCircle = `translate(${textLength})`;
          const translateAfterCircleNeg = `translate(-${textLength})`;
          const rotate180 = "rotate(180)";
          const scale = `scale(${reduceZ()})`;
                
          if (d.children) {
            return ((d.x + curR) % 360 <= 180
              ? `${translateAfterCircleNeg}${scale}`
              : `${rotate180}${translateAfterCircle}${scale}`);
          } else {
            return ((d.x + curR) % 360 <= 180
              ? `translate(8)${scale}`
              : `${rotate180}${translateBeforeCircle}${scale}`);
          }
        }
      )
    svgGroup.selectAll("circle").attr("r", NODE_DIAMETER * reduceZ());
    
  }



    },[JSON.stringify(treeData),containerRef, width, height, curX, curY, curZ, curR])
    

  return {
    containerRef,
    curR,
    curX,
    curY,
    curZ
  }
}

export default useRadialTreeInteractor